/** Legacy school-prep slug navigation helpers (hero rows still expose `checklistName` from CMS JSON). */

export const getTasklistId = (
  tasklistData: { checklistId: number; checklistName: string }[],
  currentPageName: string
): number | null => {
  const formattedPageName = currentPageName.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();

  const match = tasklistData.find((item) => item.checklistName === formattedPageName);
  return match ? match.checklistId : null;
};

export type TasklistNavItem = {
  checklistId: number;
  checklistName: string;
  description?: string;
};

export const getTasklistNavigationMeta = (
  tasklistData: TasklistNavItem[],
  currentPath: string,
  _selectedSchoolId?: string
) => {
  const filteredChecklist = tasklistData;
  const normalizedPath = currentPath.toLowerCase();
  const currentIndex = filteredChecklist.findIndex((item) =>
    normalizedPath.includes(item.checklistName?.toLowerCase().replace(/_/g, '-'))
  );
  const totalTasks = filteredChecklist.length;
  const nextItem = filteredChecklist[currentIndex + 1];
  const prevItem = filteredChecklist[currentIndex - 1];
  return {
    currentIndex,
    totalTasks,
    previousSubtitle: prevItem?.description || '',
    nextSubtitle: nextItem?.description || '',
    nextSlug: nextItem?.checklistName?.toLowerCase().replace(/_/g, '-') || '',
    prevSlug: prevItem?.checklistName?.toLowerCase().replace(/_/g, '-') || '',
  };
};

export const getLearningMaterialContent = (schoolTypeConfigured: string) => {
  const commonFaqs = [
    {
      faq_title: "What if our learning materials don't arrive before school starts?",
      faq_content:
        'Your school is aware of the timing of course placement and will accommodate a delay in accessing supplies.',
    },
    {
      faq_title: 'How many boxes of materials will we receive?',
      faq_content:
        'Students will typically receive one box per course. Students in grades K-5 usually receive four to six boxes, which will be packed into one or two larger cartons for shipment and delivery. Students in grades 6-12 are assigned semester-long courses and will receive two shipments per year—one in the fall and one in January.',
    },
    {
      faq_title: "What if there's a problem with our shipping address?",
      faq_content: 'Call or chat with our customer support team.',
    },
    {
      faq_title: 'What do we do if we need to return materials?',
      faq_content:
        'Return supplies by securing them in the boxes they came in or other sturdy packaging and printing out a pre-paid return shipping label. Labels can be requested on Help.k12.com. Make sure you secure this label to the box without covering any of the information. Take the prepared shipment to a local shipping facility.',
    },
  ];

  if (schoolTypeConfigured === 'private') {
    return {
      heroDescription:
        "Get ready to see your student unbox a great education! Your student's learning materials—textbooks, supplies for offline activities, and more—are provided at no cost to your family.*",
      suppliesDescription:
        'K12 provides curriculum and special supplies students need to complete their coursework. K12 does not supply printer ink and paper or basic items like notebooks, folders, and pens. Your teachers may have further suggestions for specific items you may want to have on hand to support learning.',
      faqs: [...commonFaqs],
      disclaimer:
        '*Computers, common household items and office supplies, like printer ink and paper, are not provided. Our enrollment consultants can help address your technology and computer questions and needs.',
    };
  }

  return {
    heroDescription:
      "Get ready to see your student unbox a great education! Your student's learning materials—a school computer, textbooks, supplies for offline activities, and more—are provided at no cost to your family.*",
    suppliesDescription:
      'K12 provides curriculum and special supplies students need to complete their coursework, often including a school computer. K12 does not supply printer ink and paper or basic items like notebooks, folders, and pens. Your teachers may have further suggestions for specific items you may want to have on hand to support learning.',
    faqs: [
      {
        faq_title: commonFaqs[0].faq_title,
        faq_content:
          'Your school is aware of the timing of course placement and will accommodate a delay in accessing supplies. Students can access online course content from a personal or public computer, such as at a public library, while waiting for learning materials.',
      },
      ...commonFaqs.slice(1),
    ],
    disclaimer:
      '*Families do not pay tuition for a student to attend an online public school. Materials provided vary by course, state, school, and grade level. Common household items and office supplies, like printer ink and paper, are not provided. Our enrollment consultants can help address your technology and computer questions and needs.',
  };
};
